import { IsStringEmpty, IsVarEmpty } from './GlobalFunctions'
import { Trim } from '../../StringFunctions'
import { PlaceInAkixiNameSpace } from './Utils'

// Browser information object

interface IInfoType {
    Name: string
    Property: object | null
    StrToSrch: string | null
    SrchStr: string | null
}

interface IBrowserData extends IInfoType {
    ID: number
    SrchStrVer: string | null
}

interface IPointerEventType {
    Flag: number
    IsSupported: boolean
}

enum iBrowserType {
    OTHER = -2,
    UNKNOWN = -1,
    IE = 0,
    FIREFOX = 1,
    NETSCAPE = 2,
    SAFARI = 3,
    KONQUEROR = 4,
    CHROME = 5
}

enum iPointerFlags {
    // Flags depicting the detected pointer input event model supported by the browser.
    STANDARD = 0x00, // Standard (e.g. just mouse events and no tablet style pointer events).
    TOUCH = 0x01, // Apple iOS compatible touch events supported, which is also supported on some Android browsers.
    MS = 0x04, // Microsoft vendor specific pointer events supported.
    W3C = 0x08 // W3C compatible pointer events supported.
}

export default class BrowserInfo {
    // Browser identifiers.
    // Browser version values.
    public static readonly flBROWSER_VERSION_IE_BECOMES_EDGE = 12 // Version that Internet Explorer becomes Microsoft Edge.
    // Whether we have detected the current browser already.
    private static m_bDetected = false
    // Browser details.
    private static m_iBrowserID: iBrowserType = iBrowserType.UNKNOWN
    private static m_sBrowser = 'Unknown'
    private static m_flVersion = 0.0
    private static m_sOS = 'Unknown'
    // The detected tablet touch input event model(s) supported by the browser.
    private static m_iPointerEventFlags: number = iPointerFlags.STANDARD

    // Browser detection information.
    private static readonly pBROWSER_DATA: IBrowserData[] = [
        {
            ID: iBrowserType.IE,
            Name: 'Edge',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'Edge',
            SrchStrVer: 'Edge'
        },
        {
            ID: iBrowserType.CHROME,
            Name: 'Chrome',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'Chrome',
            SrchStrVer: null
        },
        {
            ID: iBrowserType.OTHER,
            Name: 'OmniWeb',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'OmniWeb',
            SrchStrVer: 'OmniWeb/'
        },
        {
            ID: iBrowserType.SAFARI,
            Name: 'Safari',
            Property: null,
            StrToSrch: navigator.vendor,
            SrchStr: 'Apple',
            SrchStrVer: 'Version'
        },
        {
            ID: iBrowserType.OTHER,
            Name: 'Opera',
            Property: window.opera as OnlyNullable<object>,
            StrToSrch: null,
            SrchStr: null,
            SrchStrVer: null
        },
        {
            ID: iBrowserType.OTHER,
            Name: 'iCab',
            Property: null,
            StrToSrch: navigator.vendor,
            SrchStr: 'iCab',
            SrchStrVer: null
        },
        {
            ID: iBrowserType.KONQUEROR,
            Name: 'Konqueror',
            Property: null,
            StrToSrch: navigator.vendor,
            SrchStr: 'KDE',
            SrchStrVer: null
        },
        {
            ID: iBrowserType.FIREFOX,
            Name: 'Firefox',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'Firefox',
            SrchStrVer: null
        },
        {
            ID: iBrowserType.OTHER,
            Name: 'Camino',
            Property: null,
            StrToSrch: navigator.vendor,
            SrchStr: 'Camino',
            SrchStrVer: null
        },
        // For newer Netscapes versions (> v6).
        {
            ID: iBrowserType.NETSCAPE,
            Name: 'Netscape',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'Netscape',
            SrchStrVer: null
        },
        {
            ID: iBrowserType.IE,
            Name: 'Explorer',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'MSIE',
            SrchStrVer: 'MSIE'
        },
        {
            ID: iBrowserType.IE,
            Name: 'Explorer',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'Trident',
            SrchStrVer: 'rv'
        },
        {
            ID: iBrowserType.OTHER,
            Name: 'Mozilla',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'Gecko',
            SrchStrVer: 'rv'
        },
        // For older Netscapes version (< v4).
        {
            ID: iBrowserType.NETSCAPE,
            Name: 'Netscape',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'Mozilla',
            SrchStrVer: 'Mozilla'
        }
    ]
    // Character sequences to strip of the beginning of the browser version text embedded within the user agent text property.
    private static readonly pBROWSER_DATA_VERSIONSTRVALUE_STRIP: string[] = [':', '/']
    private static readonly iBROWSER_DATA_VERSIONSTRVALUE_STRIP_LENGTH: number =
        BrowserInfo.pBROWSER_DATA_VERSIONSTRVALUE_STRIP.length
    // O/S detection information.
    private static readonly pOS_DATA: IInfoType[] = [
        {
            Name: 'Windows',
            Property: null,
            StrToSrch: navigator.platform,
            SrchStr: 'Win'
        },
        {
            Name: 'Android',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'android'
        },
        {
            Name: 'iPhone',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'iPhone'
        },
        {
            Name: 'iPad',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'iPad'
        },
        {
            Name: 'Mac',
            Property: null,
            StrToSrch: navigator.platform,
            SrchStr: 'Mac'
        },
        {
            Name: 'iPod',
            Property: null,
            StrToSrch: navigator.userAgent,
            SrchStr: 'iPod'
        },
        {
            Name: 'Linux',
            Property: null,
            StrToSrch: navigator.platform,
            SrchStr: 'Linux'
        }
    ]
    // Supported pointer events detection information.
    private static readonly pPOINTEREVENT_DATA: IPointerEventType[] = [
        {
            Flag: iPointerFlags.TOUCH,
            IsSupported:
                'ontouchstart' in window ||
                navigator.maxTouchPoints > 0 ||
                (navigator as Navigator & { msMaxTouchPoints: number }).msMaxTouchPoints > 0
        },
        {
            Flag: iPointerFlags.MS,
            IsSupported: (navigator as Navigator & { msPointerEnabled: boolean }).msPointerEnabled
        },
        { Flag: iPointerFlags.W3C, IsSupported: (navigator as Navigator & { pointerEnabled: boolean }).pointerEnabled }
    ]

    // Searches the specified data set and returns the array index of the item whose rules are met, else -1.
    //
    private static SearchDataForMatch(pDataArray: IInfoType[]): number {
        let iFound = -1
        let pProperty
        let sStrToSrch
        let sSrchStr
        for (let i = 0; i < pDataArray.length; i += 1) {
            pProperty = pDataArray[i].Property
            sStrToSrch = pDataArray[i].StrToSrch
            sSrchStr = pDataArray[i].SrchStr
            if (!IsVarEmpty(pProperty)) {
                // Found due to specific property existence.
                iFound = i
                break
            } else if (!IsVarEmpty(sSrchStr) && !IsStringEmpty(sStrToSrch) && sStrToSrch.indexOf(sSrchStr) > -1) {
                // Found specified character string in search item's search data.
                iFound = i
                break
            }
        }
        return iFound
    }

    // Detects the current browser & O/S.
    //
    public static Detect(): void {
        if (!BrowserInfo.m_bDetected) {
            // Detect current browser.
            let iFound = BrowserInfo.SearchDataForMatch(BrowserInfo.pBROWSER_DATA)
            let sName: string
            if (iFound > -1) {
                // A matching browser was found so get the version.
                sName = BrowserInfo.pBROWSER_DATA[iFound].Name
                BrowserInfo.m_sBrowser = sName
                BrowserInfo.m_iBrowserID = BrowserInfo.pBROWSER_DATA[iFound].ID
                let sStrToSrch = BrowserInfo.pBROWSER_DATA[iFound].StrToSrch
                let sSrchStrVer = BrowserInfo.pBROWSER_DATA[iFound].SrchStrVer
                if (IsStringEmpty(sSrchStrVer)) {
                    // Use name as version search if specific version search string not specified.
                    sSrchStrVer = sName
                }

                // Find version string.
                //
                let sVersion = null
                iFound = -1
                // Search user agent string first.
                sStrToSrch = navigator.userAgent
                if (sSrchStrVer !== null && !IsStringEmpty(sStrToSrch)) {
                    iFound = sStrToSrch.indexOf(sSrchStrVer)
                }
                if (iFound < 0) {
                    // Then app version property.
                    sStrToSrch = navigator.appVersion
                    if (sSrchStrVer !== null && !IsStringEmpty(sStrToSrch)) {
                        iFound = sStrToSrch.indexOf(sSrchStrVer)
                    }
                }
                if (sSrchStrVer !== null && iFound > -1) {
                    sVersion = Trim(sStrToSrch.substring(iFound + sSrchStrVer.length))
                    if (!IsStringEmpty(sVersion)) {
                        // Strip off expected character sequences.
                        for (let i = 0; i < BrowserInfo.iBROWSER_DATA_VERSIONSTRVALUE_STRIP_LENGTH; i += 1) {
                            iFound = sVersion.indexOf(BrowserInfo.pBROWSER_DATA_VERSIONSTRVALUE_STRIP[i])
                            if (iFound === 0) {
                                sVersion = sVersion.substring(BrowserInfo.pBROWSER_DATA_VERSIONSTRVALUE_STRIP[i].length)
                                break
                            }
                        }
                        // Convert to a real number.
                        BrowserInfo.m_flVersion = parseFloat(sVersion)
                    }
                    //
                }
            }
            // Detect O/S
            iFound = BrowserInfo.SearchDataForMatch(BrowserInfo.pOS_DATA)
            if (iFound > -1) {
                // A matching browser was found so get the name.
                sName = BrowserInfo.pOS_DATA[iFound].Name
                BrowserInfo.m_sOS = sName
            }

            // Detect the tablet touch input event model(s) supported by the browser.
            //
            let pPointerEventDataCur
            for (let j = 0; j < BrowserInfo.pPOINTEREVENT_DATA.length; j += 1) {
                pPointerEventDataCur = BrowserInfo.pPOINTEREVENT_DATA[j]
                if (pPointerEventDataCur.IsSupported) {
                    /* eslint no-bitwise: "off" */
                    BrowserInfo.m_iPointerEventFlags |= pPointerEventDataCur.Flag
                }
            }

            // Mark detection as completed.
            BrowserInfo.m_bDetected = true
        }
    }

    // Returns the previously detected browser.
    //
    public static getBrowser(): string {
        return BrowserInfo.m_sBrowser
    }

    //
    public static getBrowserID(): number {
        return BrowserInfo.m_iBrowserID
    }

    // Returns the previously detected browser version.
    //
    public static getVersion(): number {
        return BrowserInfo.m_flVersion
    }

    //
    public static getVersionInt(): number {
        return Math.round(BrowserInfo.m_flVersion)
    }

    // Returns the previously detected O/S.
    //
    public static getOS(): string {
        return BrowserInfo.m_sOS
    }

    // Returns a set of iPointerFlags.XXX bit flags representing the previously detected tablet touch input event model(s) supported by the browser.
    //
    public static getPointerEventFlags(): number {
        return BrowserInfo.m_iPointerEventFlags
    }

    // Methods that returns true for specific browsers.
    //
    public static IsBrowserIE(): boolean {
        return BrowserInfo.m_iBrowserID === iBrowserType.IE
    }

    public static IsBrowserEdge(): boolean {
        return (
            BrowserInfo.m_iBrowserID === iBrowserType.IE &&
            BrowserInfo.m_flVersion >= BrowserInfo.flBROWSER_VERSION_IE_BECOMES_EDGE
        )
    }

    //
    public static IsBrowserFireFox(): boolean {
        return BrowserInfo.m_iBrowserID === iBrowserType.FIREFOX
    }

    //
    public static IsBrowserSafari(): boolean {
        return BrowserInfo.m_iBrowserID === iBrowserType.SAFARI
    }

    //
    public static IsBrowserKonqueror(): boolean {
        return BrowserInfo.m_iBrowserID === iBrowserType.KONQUEROR
    }

    //
    public static IsBrowserChrome(): boolean {
        return BrowserInfo.m_iBrowserID === iBrowserType.CHROME
    }

    // Returns true if the current browser supports background images in the option elements of a select control.
    //
    public static IsOptionImagesSupported(): boolean {
        return BrowserInfo.m_iBrowserID === iBrowserType.FIREFOX
    }
}

PlaceInAkixiNameSpace('BrowserInfo', BrowserInfo)
